import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ProfilePicCropper from '../components/ProfilePicCropper';

const API = process.env.REACT_APP_API_BASE_URL;

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    org_name: '',
    title: '',
    user_id: '',
    email: '',
    password: '',
    confirm_password: '',
    subscription_code: '',
  });
  const [picPreview, setPicPreview] = useState(null);
  const [picFile, setPicFile] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ user_id: '', email: '' });
  const [fieldChecking, setFieldChecking] = useState({ user_id: false, email: false });

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (field === 'user_id' || field === 'email') {
      setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const checkAvailability = async (field, value) => {
    if (!value.trim()) return;
    const endpoint = field === 'user_id' ? 'check-userid' : 'check-email';
    const param = field === 'user_id' ? 'user_id' : 'email';
    setFieldChecking((prev) => ({ ...prev, [field]: true }));
    try {
      const res = await axios.get(`${API}/auth/${endpoint}?${param}=${encodeURIComponent(value.trim())}`);
      if (!res.data.available) {
        setFieldErrors((prev) => ({ ...prev, [field]: res.data.message || `This ${field === 'user_id' ? 'User ID' : 'Email'} is already taken.` }));
      } else {
        setFieldErrors((prev) => ({ ...prev, [field]: '' }));
      }
    } catch {
      setFieldErrors((prev) => ({ ...prev, [field]: 'Unable to verify. Please try again.' }));
    } finally {
      setFieldChecking((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropFile(file);
    e.target.value = '';
  };

  const handleCrop = (blob) => {
    setCropFile(null);
    setPicFile(blob);
    setPicPreview(URL.createObjectURL(blob));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fieldErrors.user_id || fieldErrors.email) {
      setError('Please fix the highlighted errors before submitting.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const data = await register(form);

      // If user chose a profile pic, upload it via presigned URL
      if (picFile && data.token) {
        try {
          const api = (await import('axios')).default;
          const picRes = await api.post(
            `${process.env.REACT_APP_API_BASE_URL}/auth/profile-pic`,
            {},
            { headers: { Authorization: `Bearer ${data.token}` } },
          );
          // PUT the blob to presigned URL
          await api.put(picRes.data.upload_url, picFile, {
            headers: { 'Content-Type': 'image/jpeg' },
          });
        } catch {
          // Profile pic upload failed silently — user can retry from profile
        }
      }

      navigate('/email-pending', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex align-items-center" style={{ minHeight: '100vh', background: '#f5f7fb', padding: '2rem 0' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-sm-11 col-md-9 col-lg-7">
            <div className="text-center mb-4">
              <img
                src={process.env.PUBLIC_URL + '/img/cancepro-logo.png'}
                alt="CancePro"
                width="60"
                height="60"
                className="mb-2"
              />
              <h2 className="fw-bold">Create Account</h2>
              <p className="text-muted">Join CancePro MedX</p>
            </div>

            <div className="card">
              <div className="card-body p-4">
                {error && (
                  <div style={{ color: 'red', textAlign: 'center', fontSize: '13pt' }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input type="text" className="form-control" value={form.first_name} onChange={set('first_name')} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Middle Name</label>
                      <input type="text" className="form-control" value={form.middle_name} onChange={set('middle_name')} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input type="text" className="form-control" value={form.last_name} onChange={set('last_name')} required />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        Organisation Name <span className="text-danger">*</span>
                      </label>
                      <input type="text" className="form-control" value={form.org_name} onChange={set('org_name')} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input type="text" className="form-control" value={form.title} onChange={set('title')} placeholder="e.g. Oncologist" required />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        User ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${fieldErrors.user_id ? 'is-invalid' : form.user_id && !fieldChecking.user_id && !fieldErrors.user_id ? 'is-valid' : ''}`}
                        value={form.user_id}
                        onChange={set('user_id')}
                        onBlur={() => checkAvailability('user_id', form.user_id)}
                        placeholder="Choose a unique user ID"
                        required
                      />
                      {fieldChecking.user_id && <small className="text-muted">Checking...</small>}
                      {fieldErrors.user_id && <div className="invalid-feedback">{fieldErrors.user_id}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${fieldErrors.email ? 'is-invalid' : form.email && !fieldChecking.email && !fieldErrors.email ? 'is-valid' : ''}`}
                        value={form.email}
                        onChange={set('email')}
                        onBlur={() => checkAvailability('email', form.email)}
                        required
                      />
                      {fieldChecking.email && <small className="text-muted">Checking...</small>}
                      {fieldErrors.email && <div className="invalid-feedback">{fieldErrors.email}</div>}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <input type="password" className="form-control" value={form.password} onChange={set('password')} required />
                      <small className="text-muted">Minimum 8 characters with mixed case + number</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <input type="password" className="form-control" value={form.confirm_password} onChange={set('confirm_password')} required />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Subscription Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.subscription_code}
                      onChange={set('subscription_code')}
                      placeholder="Enter your subscription code"
                      maxLength={9}
                      required
                    />
                    <small className="text-muted">9-character code provided by your admin</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Profile Picture (optional)</label>
                    <div className="d-flex align-items-center gap-3">
                      {picPreview && (
                        <img src={picPreview} alt="Preview" className="rounded-circle" width="50" height="50" style={{ objectFit: 'cover' }} />
                      )}
                      <input
                        ref={fileRef}
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                    </div>
                    <small className="text-muted">Crop and zoom your photo before uploading</small>
                  </div>

                  {cropFile && (
                    <ProfilePicCropper
                      file={cropFile}
                      onCrop={handleCrop}
                      onCancel={() => setCropFile(null)}
                    />
                  )}

                  <div className="d-grid">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Creating account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-3">
                  <span className="text-muted">Already have an account? </span>
                  <Link to="/login">Sign In</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
